'use client';

import {
  forwardRef,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChevronDown } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { useLocale, useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import type { WalkthroughStep } from './walkthrough-steps';
import { resolveWalkthroughStepText } from './walkthrough-i18n';

const easeOut: [number, number, number, number] = [0.16, 1, 0.3, 1];

const DEFAULT_PANEL_WIDTH = 320;
const PANEL_GAP = 16;
const PANEL_VIEWPORT_MARGIN = 12;

type PanelPlacement = {
  x: number;
  y: number;
  direction: 'right' | 'bottom' | 'left' | 'top';
};

function computePlacement(
  targetRect: DOMRect,
  panelWidth: number,
  panelHeight: number,
  preference: WalkthroughStep['panelPosition'],
  padding: number
): PanelPlacement {
  const effectivePanelWidth = Math.min(
    panelWidth,
    window.innerWidth - PANEL_VIEWPORT_MARGIN * 2
  );
  const effectivePanelHeight = Math.min(
    Math.max(panelHeight, 160),
    window.innerHeight - PANEL_VIEWPORT_MARGIN * 2
  );
  const padded = {
    top: targetRect.top - padding,
    left: targetRect.left - padding,
    right: targetRect.right + padding,
    bottom: targetRect.bottom + padding,
    width: targetRect.width + padding * 2,
    height: targetRect.height + padding * 2,
  };

  const space = {
    right: window.innerWidth - padded.right,
    left: padded.left,
    bottom: window.innerHeight - padded.bottom,
    top: padded.top,
  };

  const needed = effectivePanelWidth + PANEL_GAP;
  const neededV = effectivePanelHeight + PANEL_GAP;

  // Determine direction
  let direction: PanelPlacement['direction'];
  if (preference !== 'auto') {
    const fits =
      preference === 'right'
        ? space.right >= needed
        : preference === 'left'
          ? space.left >= needed
          : preference === 'bottom'
            ? space.bottom >= neededV
            : space.top >= neededV;
    direction = fits ? preference : getBestDirection(space, needed, neededV);
  } else {
    direction = getBestDirection(space, needed, neededV);
  }

  let x: number;
  let y: number;

  // Center of target
  const targetCenterX = padded.left + padded.width / 2;
  const targetCenterY = padded.top + padded.height / 2;

  switch (direction) {
    case 'right':
      x = padded.right + PANEL_GAP;
      y = targetCenterY - effectivePanelHeight / 2;
      break;
    case 'left':
      x = padded.left - PANEL_GAP - effectivePanelWidth;
      y = targetCenterY - effectivePanelHeight / 2;
      break;
    case 'bottom':
      x = targetCenterX - effectivePanelWidth / 2;
      y = padded.bottom + PANEL_GAP;
      break;
    case 'top':
      x = targetCenterX - effectivePanelWidth / 2;
      y = padded.top - PANEL_GAP - effectivePanelHeight;
      break;
  }

  // Clamp to viewport
  x = Math.max(
    PANEL_VIEWPORT_MARGIN,
    Math.min(x, window.innerWidth - effectivePanelWidth - PANEL_VIEWPORT_MARGIN)
  );
  y = Math.max(
    PANEL_VIEWPORT_MARGIN,
    Math.min(
      y,
      window.innerHeight - effectivePanelHeight - PANEL_VIEWPORT_MARGIN
    )
  );

  return { x, y, direction };
}

function getBestDirection(
  space: { right: number; left: number; bottom: number; top: number },
  neededH: number,
  neededV: number
): PanelPlacement['direction'] {
  if (space.right >= neededH) return 'right';
  if (space.bottom >= neededV) return 'bottom';
  if (space.left >= neededH) return 'left';
  return 'top';
}

const directionOffset: Record<
  PanelPlacement['direction'],
  { x: number; y: number }
> = {
  right: { x: -12, y: 0 },
  left: { x: 12, y: 0 },
  bottom: { x: 0, y: -12 },
  top: { x: 0, y: 12 },
};

type WalkthroughPanelProps = {
  step: WalkthroughStep;
  targetRect: DOMRect;
  currentIndex: number;
  totalSteps: number;
  onNext: () => void;
  onSkip: () => void;
  onPrevious: () => void;
};

type WalkthroughNavigationProps = {
  currentIndex: number;
  totalSteps: number;
  onNext: () => void;
  onSkip: () => void;
  onPrevious: () => void;
};

type WalkthroughStepBodyProps = {
  step: WalkthroughStep;
  titleId: string;
  descriptionId: string;
  /** Wrapper class for the text region (panel makes it scrollable; inline doesn't). */
  contentClassName?: string;
};

/**
 * Shared title + body for both the floating panel and the inline (docked)
 * panel. When `step.summary` is set, the short summary shows by default and the
 * longer `description` is tucked behind a "Learn more" toggle — keeps each step
 * quick to read without losing the detail.
 */
export function WalkthroughStepBody({
  step,
  titleId,
  descriptionId,
  contentClassName,
}: WalkthroughStepBodyProps) {
  const locale = useLocale();
  const t = useTranslations('WalkthroughCommon');
  const tSteps = useTranslations('WalkthroughSteps');
  const [expanded, setExpanded] = useState(false);

  const title = resolveWalkthroughStepText(step.title, tSteps, locale, step.id);
  const description = resolveWalkthroughStepText(
    step.description,
    tSteps,
    locale,
    step.id
  );
  const summary = step.summary
    ? resolveWalkthroughStepText(step.summary, tSteps, locale, step.id)
    : null;

  const learnMore = t.has('learnMore') ? t('learnMore') : 'Learn more';
  const showLess = t.has('showLess') ? t('showLess') : 'Show less';

  return (
    <>
      <h3 id={titleId} className="shrink-0 text-sm font-semibold">
        {title}
      </h3>
      <div className={contentClassName}>
        <p
          id={descriptionId}
          className="text-muted-foreground mt-1.5 text-[13px] leading-relaxed"
        >
          {summary ?? description}
        </p>
        {summary ? (
          <>
            {expanded ? (
              <p className="text-muted-foreground mt-2 text-[13px] leading-relaxed">
                {description}
              </p>
            ) : null}
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              aria-expanded={expanded}
              className="text-primary focus-visible:ring-primary/40 mt-2 inline-flex items-center gap-0.5 rounded text-[12px] font-medium hover:underline focus:outline-none focus-visible:ring-2"
            >
              {expanded ? showLess : learnMore}
              <ChevronDown
                className={`size-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
              />
            </button>
          </>
        ) : null}
      </div>
    </>
  );
}

export function WalkthroughNavigation({
  currentIndex,
  totalSteps,
  onNext,
  onSkip,
  onPrevious,
}: WalkthroughNavigationProps) {
  const t = useTranslations('WalkthroughCommon');
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex >= totalSteps - 1;

  return (
    <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex max-w-full flex-wrap gap-1">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all duration-200 ${
              i === currentIndex
                ? 'bg-primary w-4'
                : 'bg-muted-foreground/30 w-1.5'
            }`}
          />
        ))}
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onPrevious}
          disabled={isFirstStep}
        >
          {t('back')}
        </Button>
        <Button variant="ghost" size="sm" onClick={onSkip}>
          {t('skip')}
        </Button>
        <Button size="sm" onClick={onNext}>
          {isLastStep ? t('done') : t('next')}
        </Button>
      </div>
    </div>
  );
}

export const WalkthroughPanel = forwardRef<
  HTMLDivElement,
  WalkthroughPanelProps
>(function WalkthroughPanel(
  { step, targetRect, currentIndex, totalSteps, onNext, onSkip, onPrevious },
  forwardedRef
) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const t = useTranslations('WalkthroughCommon');
  const prefersReducedMotion = useReducedMotion();
  const [panelHeight, setPanelHeight] = useState(0);
  const panelWidth = step.panelWidth ?? DEFAULT_PANEL_WIDTH;

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const measure = () => {
      const nextHeight = Math.ceil(panel.getBoundingClientRect().height);
      setPanelHeight((previous) =>
        previous === nextHeight ? previous : nextHeight
      );
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(panel);

    return () => resizeObserver.disconnect();
  }, [step.id]);

  const placement = useMemo(
    () =>
      computePlacement(
        targetRect,
        panelWidth,
        panelHeight,
        step.panelPosition,
        step.padding
      ),
    [targetRect, panelWidth, panelHeight, step.panelPosition, step.padding]
  );

  const offset = directionOffset[placement.direction] ?? { x: 0, y: 0 };

  return (
    <motion.div
      ref={(node) => {
        panelRef.current = node;
        if (!forwardedRef) return;
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
          return;
        }
        forwardedRef.current = node;
      }}
      data-slot="walkthrough-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      aria-keyshortcuts="ArrowLeft ArrowRight Escape"
      tabIndex={-1}
      layoutId={step.sharedPanelLayoutId}
      className="bg-popover text-popover-foreground focus-visible:ring-primary/30 pointer-events-auto absolute flex max-h-[calc(100dvh-24px)] flex-col rounded-xl border p-3 shadow-xl focus:outline-none focus-visible:ring-2 sm:p-4"
      style={{
        visibility: panelHeight === 0 ? 'hidden' : 'visible',
        width: `min(${panelWidth}px, calc(100vw - ${PANEL_VIEWPORT_MARGIN * 2}px))`,
      }}
      initial={{
        opacity: 0,
        x: placement.x + offset.x * 2,
        y: placement.y + offset.y * 2,
        scale: 0.92,
      }}
      animate={{
        opacity: 1,
        x: placement.x,
        y: placement.y,
        scale: 1,
      }}
      exit={{
        opacity: 0,
        x: placement.x + offset.x,
        y: placement.y + offset.y,
        scale: 0.95,
      }}
      transition={
        prefersReducedMotion
          ? { duration: 0 }
          : { duration: 0.22, ease: easeOut }
      }
    >
      <WalkthroughStepBody
        step={step}
        titleId={titleId}
        descriptionId={descriptionId}
        contentClassName="min-h-0 flex-1 overflow-y-auto pr-1"
      />
      <p className="sr-only">{t('instructions')}</p>
      <WalkthroughNavigation
        currentIndex={currentIndex}
        totalSteps={totalSteps}
        onNext={onNext}
        onPrevious={onPrevious}
        onSkip={onSkip}
      />
    </motion.div>
  );
});
